import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { QueryPanel } from '@/features/QueryPanel';
import { SystemOverview } from '@/features/SystemOverview';
import { ActionableItems } from '@/components/ActionableItems';
import { EventFeed } from '@/features/EventFeed';
import {
    useOverlayManager,
    OverlayNames,
    useDocumentTitle,
    useResetQueryState,
    useFeatureFlags,
} from '@/hooks';
import { useCameraLimitReached } from '@/features/SettingsView/hooks/useCameraLimitReached';
import { AlertActionItem } from '@/types/alert';
import { Permission } from '@/types/permissions';
import { AppRoutes } from '@/constants/routes';
import { I18nNamespace } from '@/lib/i18n-namespaces';

export default function HomePage() {
    useResetQueryState();
    useDocumentTitle('home');
    const navigate = useNavigate();
    const { t } = useTranslation(I18nNamespace.HOME);
    const { open } = useOverlayManager();
    const isCameraLimitReached = useCameraLimitReached();
    const { videoFilesEnabled, liveCamerasEnabled, alertsEnabled } =
        useFeatureFlags();

    const customActions: AlertActionItem[] = [
        ...(videoFilesEnabled
            ? [
                  {
                      title: t('actions.addNewFile'),
                      onClick: () => open(OverlayNames.SOURCE_UPLOAD, {}),
                      shortcut: ['F'],
                      permission: Permission.CREATE,
                  } satisfies AlertActionItem,
              ]
            : []),
        ...(liveCamerasEnabled && !isCameraLimitReached
            ? [
                  {
                      title: t('actions.addNewCamera'),
                      onClick: () => open(OverlayNames.ADD_CAMERA, {}),
                      shortcut: ['C'],
                      permission: Permission.CREATE,
                  } satisfies AlertActionItem,
              ]
            : []),
        ...(alertsEnabled
            ? [
                  {
                      title: t('actions.newAlertRule'),
                      onClick: () => navigate(AppRoutes.ALERT_RULES_CREATE),
                      shortcut: ['R'],
                      permission: Permission.CREATE,
                  } satisfies AlertActionItem,
              ]
            : []),
    ];

    return (
        <div className="h-full overflow-y-auto px-4 panels:px-8">
            <EventFeed context={{ type: 'recentAlerts' }} />
            <div className="pt-[15vh] pb-4 flex flex-col gap-8 max-w-content mx-auto w-full min-h-full">
                <SystemOverview />

                <div className="flex flex-col gap-4">
                    <h4 className="text-h4 text-white">{t('searchPrompt')}</h4>
                    <QueryPanel />
                </div>

                <div className="flex-1" />

                <div className="pt-4">
                    <ActionableItems actions={customActions} />
                </div>
            </div>
        </div>
    );
}
